// src/ui/screens/Settings.tsx
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { deleteAllLocalData, getStorageEstimate } from "../../storage/db";
import { isAnalyticsOptedOut, setAnalyticsOptOut, track } from "../../services/analytics";
import { colors } from "../design-system/colors";
import { fontFamily, typeScale } from "../design-system/typography";
import { space, radius, minTapTarget } from "../design-system/spacing";

export default function Settings() {
  const navigate = useNavigate();
  const [analyticsOptOut, setLocalOptOut] = useState(isAnalyticsOptedOut());
  const [storageRatio, setStorageRatio] = useState<number | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  useEffect(() => {
    track("settings_opened");
    void getStorageEstimate().then((r) => setStorageRatio(r.usageRatio));
  }, []);

  const handleToggleAnalytics = () => {
    const next = !analyticsOptOut;
    setAnalyticsOptOut(next);
    setLocalOptOut(next);
  };

  const handleDeleteAll = async () => {
    if (!confirmingDelete) {
      setConfirmingDelete(true);
      return;
    }
    await deleteAllLocalData();
    track("data_deleted");
    setConfirmingDelete(false);
    navigate("/measure");
  };

  return (
    <div style={styles.container}>
      <button onClick={() => navigate("/measure")} style={styles.backButton}>
        ← 戻る
      </button>
      <h1 style={styles.title}>設定</h1>

      <Section title="ストレージ">
        <Row
          label="ローカル保存容量"
          value={storageRatio !== null ? `${Math.round(storageRatio * 100)}% 使用中` : "計測不可"}
        />
      </Section>

      <Section title="プライバシー">
        <ToggleRow
          label="匿名利用統計を送信する"
          description="映像・骨格データ・氏名等は含まれません。オフにするといつでも停止できます。"
          checked={!analyticsOptOut}
          onChange={handleToggleAnalytics}
        />
        <button
          style={{
            ...styles.dangerButton,
            background: confirmingDelete ? colors.danger : "transparent",
            color: confirmingDelete ? colors.bg : colors.danger,
          }}
          onClick={handleDeleteAll}
        >
          {confirmingDelete ? "本当にすべて削除する" : "すべてのデータを削除"}
        </button>
      </Section>

      <Section title="法的情報">
        <LinkRow label="プライバシーポリシー" href="/docs/PRIVACY_POLICY.md" />
        <LinkRow label="利用規約" href="/docs/TERMS_OF_SERVICE.md" />
        <LinkRow label="免責事項(医療アドバイスではありません)" href="/docs/DISCLAIMER.md" />
      </Section>

      <p style={styles.version}>KinemaLens AI v2.0.0</p>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: space.xl }}>
      <div style={styles.sectionTitle}>{title}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: space.sm }}>{children}</div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={styles.row}>
      <span style={styles.rowLabel}>{label}</span>
      <span style={styles.rowValue}>{value}</span>
    </div>
  );
}

function ToggleRow({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <div style={styles.row}>
      <div style={{ flex: 1 }}>
        <div style={styles.rowLabel}>{label}</div>
        <div style={styles.rowDescription}>{description}</div>
      </div>
      <button
        role="switch"
        aria-checked={checked}
        onClick={onChange}
        style={{
          ...styles.switchTrack,
          background: checked ? colors.amber : colors.surfaceBorder,
        }}
      >
        <span
          style={{
            ...styles.switchThumb,
            transform: checked ? "translateX(20px)" : "translateX(0)",
          }}
        />
      </button>
    </div>
  );
}

function LinkRow({ label, href }: { label: string; href: string }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" style={styles.linkRow}>
      {label}
    </a>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: "100dvh",
    background: colors.bg,
    color: colors.textPrimary,
    fontFamily: fontFamily.ui,
    padding: space.lg,
    maxWidth: 480,
    margin: "0 auto",
  },
  backButton: {
    background: "none",
    border: "none",
    color: colors.textSecondary,
    fontFamily: fontFamily.ui,
    fontSize: typeScale.body.size,
    padding: 0,
    marginBottom: space.md,
    minHeight: minTapTarget,
    cursor: "pointer",
  },
  title: { fontSize: typeScale.h1.size, marginBottom: space.lg },
  sectionTitle: {
    fontSize: typeScale.caption.size,
    color: colors.textMuted,
    marginBottom: space.sm,
  },
  row: {
    display: "flex",
    alignItems: "center",
    minHeight: minTapTarget,
    padding: space.md,
    borderRadius: radius.md,
    background: colors.surface,
    border: `1px solid ${colors.surfaceBorder}`,
  },
  rowLabel: { fontSize: typeScale.body.size, color: colors.textPrimary },
  rowDescription: { fontSize: typeScale.caption.size, color: colors.textSecondary, marginTop: 2 },
  rowValue: { fontFamily: fontFamily.numeric, color: colors.textSecondary, marginLeft: "auto" },
  switchTrack: {
    width: 44,
    height: 26,
    borderRadius: radius.full,
    border: "none",
    position: "relative",
    cursor: "pointer",
    flexShrink: 0,
  },
  switchThumb: {
    position: "absolute",
    top: 3,
    left: 3,
    width: 20,
    height: 20,
    borderRadius: radius.full,
    background: colors.textPrimary,
    transition: "transform 0.15s ease-out",
  },
  dangerButton: {
    minHeight: minTapTarget,
    borderRadius: radius.md,
    border: `1.5px solid ${colors.danger}`,
    fontFamily: fontFamily.ui,
    fontSize: typeScale.body.size,
    cursor: "pointer",
  },
  linkRow: {
    display: "block",
    minHeight: minTapTarget,
    lineHeight: `${minTapTarget}`,
    padding: `0 ${space.md}`,
    borderRadius: radius.md,
    background: colors.surface,
    border: `1px solid ${colors.surfaceBorder}`,
    color: colors.textSecondary,
    textDecoration: "none",
    fontSize: typeScale.body.size,
  },
  version: { textAlign: "center", color: colors.textMuted, fontSize: typeScale.caption.size, marginTop: space.xl },
};
